import { clamp01, type V2 } from '../core/math';
import { DISTRICTS } from '../world/lowBorough/data';
import type { Mixer } from './mixer';
import { bellPartials, noiseBurst, ringTick, tone } from './synth';

/**
 * The borough's soundscape: looping beds and sparse events, mixed by WHERE the listener is (a smooth
 * blend of the districts within ~45 m, so walking over a boundary cross-fades) and WHEN it is (the
 * borough clock, or the Settings -> Time of day choice when it holds a mood).
 *
 * Beds: distant traffic (by district density and hour), city hiss, wind, rain on the street / on the
 * car roof, pub murmur by an open door; by district: water lapping and a crane motor at the docks,
 * market crowd murmur on the Parade, wind in the trees on Mercy Square, the gasworks hum in Cinder
 * Yards. Events: ship horns, rope creaks, gulls and cranes at the docks; barrow calls and pub doors
 * on the Parade; birds, children and the church clock on Mercy Square; clanks and trains over the
 * viaduct in the Yards; dogs, a far siren and the train at night anywhere. Non-deterministic on
 * purpose (Math.random): audio is exempt from the sim determinism rule.
 */

export type AmbientEvent = 'shipHorn' | 'train' | 'gull' | 'birds' | 'dog' | 'farSiren' | 'clank' | 'laugh' | 'rope' | 'crane' | 'barrow' | 'pubDoor' | 'children' | 'bottles';

export const AMBIENT_EVENTS: readonly AmbientEvent[] = ['shipHorn', 'train', 'gull', 'birds', 'dog', 'farSiren', 'clank', 'laugh', 'rope', 'crane', 'barrow', 'pubDoor', 'children', 'bottles'];

/** Gulls/birds are daytime-only (06:00 - 19:00). */
export function isDaytime(hour: number): boolean {
  return hour >= 6 && hour < 19;
}

/** Pubs are open (and audible) 11:00 - 23:30. */
export function pubOpen(hour: number): boolean {
  return hour >= 11 && hour < 23.5;
}

/** Market stalls trade 08:00 - 17:00. */
export function marketOpen(hour: number): boolean {
  return hour >= 8 && hour < 17;
}

function smooth(x: number): number {
  const t = clamp01(x);
  return t * t * (3 - 2 * t);
}

/** 0 at night, 1 by day, with 90-minute dawn (05:30-07:00) and dusk (18:00-19:30) ramps. */
export function dayness(hour: number): number {
  const h = ((hour % 24) + 24) % 24;
  if (h < 5.5 || h >= 19.5) return 0;
  if (h < 7) return smooth((h - 5.5) / 1.5);
  if (h < 18) return 1;
  return 1 - smooth((h - 18) / 1.5);
}

export interface TimeOfDayLike {
  setting: 'auto' | 'day' | 'dusk' | 'night';
  hour: number;
  forced: 'overcast' | 'dusk' | 'night' | null;
}

/** The hour the soundscape should follow: the clock on AUTO, a representative hour for a held mood. */
export function audioHour(t: TimeOfDayLike | undefined, fallback: number): number {
  if (!t) return fallback;
  if (t.forced) return t.forced === 'overcast' ? 13 : t.forced === 'dusk' ? 19.2 : 23.5;
  if (t.setting === 'auto') return t.hour;
  return t.setting === 'day' ? 13 : t.setting === 'dusk' ? 19.2 : 23.5;
}

// ---------------------------------------------------------------------------------------------
// Where

export type DistrictWeights = Record<string, number>;

/** Metres over which a district's sound fades in as you approach its boundary. */
export const DISTRICT_BLEND = 45;

interface Rect {
  minX: number;
  minZ: number;
  maxX: number;
  maxZ: number;
}

/**
 * Normalised weights of the districts around a point: 1 inside, fading over `DISTRICT_BLEND` outside
 * each rectangle, so a listener near a boundary hears both sides.
 */
export function districtWeights(pos: V2, districts: readonly { id: string; bounds: Rect }[] = DISTRICTS): DistrictWeights {
  const w: DistrictWeights = {};
  let total = 0;
  for (const d of districts) {
    const b = d.bounds;
    const dx = Math.max(b.minX - pos.x, 0, pos.x - b.maxX);
    const dz = Math.max(b.minZ - pos.z, 0, pos.z - b.maxZ);
    const dist = Math.hypot(dx, dz);
    const v = smooth(1 - dist / DISTRICT_BLEND);
    if (v > 0) {
      w[d.id] = v;
      total += v;
    }
  }
  if (total <= 0) {
    // Off the map: the nearest district.
    let best = '';
    let bd = Infinity;
    for (const d of districts) {
      const b = d.bounds;
      const dx = Math.max(b.minX - pos.x, 0, pos.x - b.maxX);
      const dz = Math.max(b.minZ - pos.z, 0, pos.z - b.maxZ);
      const dist = Math.hypot(dx, dz);
      if (dist < bd) {
        bd = dist;
        best = d.id;
      }
    }
    if (best) w[best] = 1;
    return w;
  }
  for (const k of Object.keys(w)) w[k] = (w[k] as number) / total;
  return w;
}

/** Per-district event colour (multiplies the base weights). */
const DISTRICT_EVENTS: Record<string, Partial<Record<AmbientEvent, number>>> = {
  'gullet-basin': { shipHorn: 3, gull: 2.5, clank: 1, rope: 2.5, crane: 2, dog: 0.6, birds: 0.2 },
  'cinder-yards': { train: 2.5, clank: 3, gull: 0.4, dog: 1.4, birds: 0.3, farSiren: 1.3 },
  'the-parade': { laugh: 1.6, pubDoor: 2.5, barrow: 3, shipHorn: 0.4, birds: 0.5, gull: 0.4, farSiren: 1.2 },
  'tanners-row': { dog: 1.8, birds: 1.3, bottles: 2.5, children: 1, shipHorn: 0.5, gull: 0.3 },
  'mercy-square': { birds: 2.2, children: 2.5, gull: 0.5, shipHorn: 0.5, dog: 0.5, laugh: 0.4 },
  'kiln-common': { train: 2, barrow: 2, children: 1.6, birds: 1.6, dog: 1.2, clank: 1.2, pubDoor: 1.2, gull: 0.3, shipHorn: 0.3 },
};

/** Base weight of each event by hour (before district colour). Zero rules an event out. */
function baseWeight(e: AmbientEvent, hour: number): number {
  const day = dayness(hour);
  const night = 1 - day;
  switch (e) {
    case 'shipHorn':
      return 1;
    case 'train':
      return 0.7 + 0.5 * night;
    case 'gull':
      return 2.2 * day;
    case 'birds':
      return 2 * day;
    case 'dog':
      return 1.3 * night;
    case 'farSiren':
      return 0.9 * night;
    case 'clank':
      return 0.4 + 0.5 * day;
    case 'laugh':
      return pubOpen(hour) ? 0.4 + 0.6 * night : 0;
    case 'rope':
      return 0.8;
    case 'crane':
      return 0.3 + 0.7 * day;
    case 'barrow':
      return marketOpen(hour) ? 1 : 0;
    case 'pubDoor':
      return pubOpen(hour) ? 0.5 + 0.5 * night : 0;
    case 'children':
      return hour >= 8 && hour < 20 ? day : 0;
    case 'bottles':
      return hour >= 5 && hour < 8 ? 1 : hour >= 22 || hour < 1 ? 0.6 : 0;
  }
}

/** Event weights at an hour for a district blend (or one district id, or the generic borough mix). */
export function ambientWeights(hour: number, where?: DistrictWeights | string): Record<AmbientEvent, number> {
  const w = {} as Record<AmbientEvent, number>;
  const blend: DistrictWeights | null = typeof where === 'string' ? { [where]: 1 } : where ?? null;
  for (const e of AMBIENT_EVENTS) {
    const base = baseWeight(e, hour);
    if (!blend) {
      // The generic mix: the classic borough events only.
      w[e] = e === 'shipHorn' || e === 'train' || e === 'gull' || e === 'birds' || e === 'dog' || e === 'farSiren' ? base : 0;
      continue;
    }
    let colour = 0;
    for (const [id, v] of Object.entries(blend)) {
      const c = DISTRICT_EVENTS[id]?.[e];
      // Events without an entry for a district play there at a low generic rate; the district-specific
      // ones (rope, crane, barrow, pubDoor, children, bottles, clank, laugh) only where listed.
      const generic = e === 'rope' || e === 'crane' || e === 'barrow' || e === 'pubDoor' || e === 'children' || e === 'bottles' || e === 'clank' || e === 'laugh' ? 0 : 1;
      colour += v * (c ?? generic);
    }
    w[e] = base * colour;
  }
  return w;
}

/** Weighted pick from a uniform `u` in [0,1). Returns null if every weight is zero. */
export function pickAmbientEvent(hour: number, u: number, where?: DistrictWeights | string): AmbientEvent | null {
  const w = ambientWeights(hour, where);
  let total = 0;
  for (const e of AMBIENT_EVENTS) total += w[e];
  if (total <= 0) return null;
  let x = clamp01(u) * total;
  for (const e of AMBIENT_EVENTS) {
    x -= w[e];
    if (x < 0) return e;
  }
  return AMBIENT_EVENTS[AMBIENT_EVENTS.length - 1] as AmbientEvent;
}

/** Seconds until the next ambient event, from uniform `u` (7 .. 22 s; the Parade by day is busier). */
export function nextAmbientDelay(u: number, busy = 0): number {
  const scale = 1 - 0.35 * clamp01(busy);
  return (7 + clamp01(u) * 15) * scale;
}

/** Ambience bed level: dips while driving fast so engines and the radio sit on top. */
export function ambienceLevel(driving: boolean, speed: number): number {
  return driving ? 1 - 0.45 * clamp01(speed / 28) : 1;
}

/** Distant-traffic bed level by district traffic density (0..1) and hour (quiet small hours). */
export function trafficLevel(density: number, hour: number): number {
  const h = ((hour % 24) + 24) % 24;
  const night = h < 5.5 ? 0.35 : h < 7 ? 0.35 + 0.65 * ((h - 5.5) / 1.5) : h >= 23 ? 0.5 : 1;
  return (0.1 + 0.16 * clamp01(density)) * night;
}

export interface BedLevels {
  water: number;
  crane: number;
  hum: number;
  crowd: number;
  trees: number;
}

/** District bed gains for a blend at an hour (pure; what `update` fades the beds towards). */
export function bedLevels(w: DistrictWeights, hour: number, driving: boolean): BedLevels {
  const day = dayness(hour);
  const g = (id: string): number => w[id] ?? 0;
  const inside = driving ? 0.55 : 1;
  return {
    water: 0.12 * g('gullet-basin') * inside,
    crane: 0.05 * g('gullet-basin') * (0.4 + 0.6 * day) * inside,
    hum: (0.07 * g('cinder-yards') + 0.02 * g('gullet-basin')) * inside,
    crowd: (marketOpen(hour) ? 0.15 : 0.05 * day) * g('the-parade') * inside,
    trees: (0.075 * g('mercy-square') + 0.03 * g('tanners-row')) * inside,
  };
}

export interface AmbienceContext {
  hour: number;
  driving: boolean;
  speed: number;
  /** District id at the listener ('' if unknown). */
  district: string;
  /** District traffic density 0..1. */
  traffic: number;
  /** 0 dry, ~0.5 drizzle, 1 rain. */
  wet: number;
  /** 0..1 how close the listener is to an open pub/club door. */
  pub: number;
  /** Listener position (district blend). */
  pos: V2;
}

/** Minimum real time between two church-clock strikes (s). */
export const BELL_REST = 180;

const r = (lo: number, hi: number): number => lo + Math.random() * (hi - lo);

interface Beds {
  level: GainNode;
  traffic: GainNode;
  hiss: GainNode;
  wind: GainNode;
  rain: GainNode;
  roof: GainNode;
  pub: GainNode;
  water: GainNode;
  crane: GainNode;
  hum: GainNode;
  crowd: GainNode;
  trees: GainNode;
  nodes: AudioNode[];
}

export class Ambience {
  private beds: Beds | null = null;
  private timer = r(3, 8);
  private dropClock = 0;
  private lastHour = -1;
  /** Real seconds until the church clock may strike again (a fast in-game clock must not make it nag). */
  private bellRest = 0;
  private readonly cleanups = new Set<ReturnType<typeof setTimeout>>();
  private ctxState: AmbienceContext = { hour: 12, driving: false, speed: 0, district: '', traffic: 0.5, wet: 0, pub: 0, pos: { x: 0, z: 0 } };
  private weights: DistrictWeights = {};
  private weightClock = 0;
  /** Last event played (for diagnostics). */
  lastEvent: AmbientEvent | 'churchBell' | null = null;

  constructor(private readonly mx: Mixer) {}

  /** Current district blend (diagnostics). */
  get blend(): DistrictWeights {
    return this.weights;
  }

  private build(): Beds {
    const mx = this.mx;
    const ctx = mx.ctx as AudioContext;
    const t = ctx.currentTime;
    const nodes: AudioNode[] = [];
    const reg = <T extends AudioNode>(n: T): T => {
      nodes.push(n);
      return n;
    };
    const gain = (v: number): GainNode => {
      const g = reg(ctx.createGain());
      g.gain.value = v;
      return g;
    };
    const filt = (type: BiquadFilterType, f: number, q = 0.7): BiquadFilterNode => {
      const b = reg(ctx.createBiquadFilter());
      b.type = type;
      b.frequency.value = f;
      b.Q.value = q;
      return b;
    };
    const lfo = (hz: number, depth: number, target: AudioParam, type: OscillatorType = 'sine'): void => {
      const o = reg(ctx.createOscillator());
      o.type = type;
      o.frequency.value = hz;
      const d = gain(depth);
      o.connect(d).connect(target);
      o.start(t);
    };
    const src = (kind: 'white' | 'pink' | 'brown'): AudioBufferSourceNode => {
      const s = reg(mx.noiseSource(kind));
      mx.startNoise(s, t);
      return s;
    };
    const level = gain(1);
    level.connect(mx.buses.ambience);

    // Distant traffic: a low rumble that swells and recedes. The swell is a separate unity gain stage
    // modulated by a fraction (+-25 %), so it scales with the level: an absolute LFO on the level gain
    // pumped the bed +-6 dB by day and down to silence at night, when the level is only ~0.09.
    const traffic = gain(0.2);
    const swell = gain(1);
    src('brown').connect(filt('lowpass', 180)).connect(swell).connect(traffic).connect(level);
    lfo(0.07, 0.16, swell.gain);
    lfo(0.031, 0.09, swell.gain);

    const hiss = gain(0.02);
    src('pink').connect(filt('highpass', 1500)).connect(filt('lowpass', 6000)).connect(hiss).connect(level);

    const wind = gain(0.03);
    const windBand = filt('bandpass', 420, 0.6);
    src('pink').connect(windBand).connect(wind).connect(level);
    lfo(0.09, 160, windBand.frequency);
    lfo(0.05, 0.015, wind.gain);

    // Rain on the street: a soft broadband wash (droplets are scheduled on top).
    const rain = gain(0);
    src('pink').connect(filt('highpass', 900, 0.5)).connect(filt('lowpass', 7000, 0.5)).connect(rain).connect(level);

    // Rain drumming on the car roof: a lower, fuller band with a fast irregular flutter.
    const roof = gain(0);
    const roofAm = gain(0.75);
    src('white').connect(filt('bandpass', 1700, 0.6)).connect(roofAm).connect(roof).connect(level);
    src('brown').connect(filt('lowpass', 500)).connect(gain(0.6)).connect(roof);
    lfo(13.7, 0.2, roofAm.gain, 'triangle');
    lfo(7.1, 0.12, roofAm.gain);

    // Murmur: voice-formant bands of noise, each "talking" (fast irregular AM). Two instances: a pub
    // behind a door (darker) and the market crowd (open air, brighter, denser).
    const murmur = (bands: readonly (readonly [number, number, number, number])[], lp: number): GainNode => {
      const out = gain(0);
      const dark = filt('lowpass', lp, 0.5);
      dark.connect(out).connect(level);
      for (const [f, q, rate1, rate2] of bands) {
        const band = filt('bandpass', f, q);
        const am = gain(0.5);
        src('pink').connect(band).connect(am).connect(dark);
        lfo(rate1, 0.3, am.gain);
        lfo(rate2, 0.2, am.gain, 'triangle');
        lfo(0.13 + rate1 * 0.01, 120, band.frequency);
      }
      return out;
    };
    const pub = murmur(
      [
        [520, 2.2, 3.1, 4.7],
        [1350, 3, 3.9, 5.6],
        [820, 2.6, 2.4, 6.3],
      ],
      2200,
    );
    const crowd = murmur(
      [
        [480, 2, 2.7, 4.1],
        [900, 2.4, 3.6, 5.9],
        [1500, 3, 4.4, 6.8],
        [2400, 4, 2.1, 7.7],
      ],
      3800,
    );

    // Water lapping against the quay: low filtered noise, slow swell, a lick of spray on top.
    const water = gain(0);
    const lap = filt('lowpass', 420, 0.6);
    const lapAm = gain(0.6);
    src('pink').connect(lap).connect(lapAm).connect(water).connect(level);
    lfo(0.31, 0.35, lapAm.gain);
    lfo(0.17, 0.25, lapAm.gain, 'triangle');
    lfo(0.09, 120, lap.frequency);
    const spray = filt('bandpass', 2600, 0.8);
    const sprayAm = gain(0.25);
    src('white').connect(spray).connect(sprayAm).connect(water);
    lfo(0.27, 0.2, sprayAm.gain, 'triangle');

    // A crane motor somewhere on the quay: a low whine that works and rests.
    const crane = gain(0);
    const motor = reg(ctx.createOscillator());
    motor.type = 'sawtooth';
    motor.frequency.value = 92;
    lfo(0.023, 7, motor.frequency);
    const motorAm = gain(0.5);
    motor.connect(filt('lowpass', 520, 1.2)).connect(motorAm).connect(crane).connect(level);
    lfo(0.045, 0.45, motorAm.gain);
    motor.start(t);

    // Gasworks / yard hum: mains harmonics with a little brown rumble.
    const hum = gain(0);
    for (const [f, g] of [
      [50, 0.5],
      [100, 0.35],
      [150, 0.15],
    ] as const) {
      const o = reg(ctx.createOscillator());
      o.frequency.value = f;
      o.connect(gain(g)).connect(hum);
      o.start(t);
    }
    src('brown').connect(filt('lowpass', 120)).connect(gain(0.5)).connect(hum);
    const humAm = gain(1);
    hum.connect(humAm).connect(level);
    lfo(0.3, 0.1, humAm.gain);

    // Wind in the plane trees: a bright rustle that gusts.
    const trees = gain(0);
    const leaf = filt('bandpass', 2800, 0.5);
    const leafAm = gain(0.5);
    src('white').connect(leaf).connect(leafAm).connect(trees).connect(level);
    lfo(0.08, 0.4, leafAm.gain);
    lfo(0.23, 0.2, leafAm.gain, 'triangle');
    lfo(0.11, 700, leaf.frequency);

    return { level, traffic, hiss, wind, rain, roof, pub, water, crane, hum, crowd, trees, nodes };
  }

  update(dt: number, c: AmbienceContext): void {
    const mx = this.mx;
    this.ctxState = c;
    if (!mx.ready || !mx.running) return;
    const beds = this.beds ?? (this.beds = this.build());
    const now = mx.now();
    const night = 1 - dayness(c.hour);
    const wet = clamp01(c.wet);
    this.weightClock -= dt;
    if (this.weightClock <= 0) {
      this.weightClock = 0.4;
      this.weights = districtWeights(c.pos);
    }
    beds.level.gain.setTargetAtTime(ambienceLevel(c.driving, c.speed), now, 0.6);
    beds.traffic.gain.setTargetAtTime(trafficLevel(c.traffic, c.hour), now, 2);
    beds.hiss.gain.setTargetAtTime(0.02 - 0.008 * night + 0.012 * wet, now, 2);
    beds.wind.gain.setTargetAtTime(0.03 + 0.02 * wet, now, 2);
    // Inside the car the street rain is muffled and the roof takes over.
    beds.rain.gain.setTargetAtTime(wet * (c.driving ? 0.025 : 0.07), now, 1.2);
    beds.roof.gain.setTargetAtTime(c.driving ? wet * 0.06 * (1 - 0.3 * clamp01(c.speed / 30)) : 0, now, 0.5);
    beds.pub.gain.setTargetAtTime(pubOpen(c.hour) ? clamp01(c.pub) * (c.driving ? 0.03 : 0.08) : 0, now, 0.8);
    const lv = bedLevels(this.weights, c.hour, c.driving);
    const fade = 1.5;
    beds.water.gain.setTargetAtTime(lv.water * (1 - 0.3 * wet), now, fade);
    beds.crane.gain.setTargetAtTime(lv.crane, now, fade);
    beds.hum.gain.setTargetAtTime(lv.hum, now, fade);
    beds.crowd.gain.setTargetAtTime(lv.crowd * (1 - 0.5 * wet), now, fade);
    beds.trees.gain.setTargetAtTime(lv.trees * (1 + 0.6 * wet), now, fade);

    // Raindrops: plinks on the street (or fat drops on the roof).
    if (wet > 0.05) {
      this.dropClock -= dt;
      while (this.dropClock <= 0) {
        this.dropClock += 1 / (wet * (c.driving ? 9 : 14));
        if (mx.voiceCount < 30) this.drop(c.driving);
      }
    }

    // The church clock strikes the hour (quietly, from across the borough).
    const hourInt = Math.floor(c.hour);
    this.bellRest = Math.max(0, this.bellRest - dt);
    if (this.lastHour >= 0 && hourInt !== this.lastHour && c.hour - hourInt < 0.2 && this.bellRest <= 0) {
      this.bellRest = BELL_REST;
      this.churchBell(hourInt);
    }
    this.lastHour = hourInt;

    this.timer -= dt;
    if (this.timer <= 0) {
      const busy = (this.weights['the-parade'] ?? 0) * (marketOpen(c.hour) ? 1 : 0.4);
      this.timer = nextAmbientDelay(Math.random(), busy);
      const e = pickAmbientEvent(c.hour, Math.random(), Object.keys(this.weights).length ? this.weights : c.district || undefined);
      if (e) this.play(e);
    }
  }

  private drop(onRoof: boolean): void {
    const mx = this.mx;
    const ctx = mx.ctx;
    if (!ctx) return;
    const t = ctx.currentTime + Math.random() * 0.05;
    const d = mx.buses.ambience;
    if (onRoof) ringTick(mx, d, t, r(900, 2200), 3, r(0.015, 0.04), 0.03);
    else ringTick(mx, d, t, r(2500, 6500), 6, r(0.006, 0.02), 0.025);
  }

  /** Output chain for a far-off event: gain -> lowpass -> pan -> ambience bus; freed after `life` s. */
  private chain(level: number, life: number, cutoff: number, build: (dest: AudioNode, t: number, pan: StereoPannerNode) => void): void {
    const mx = this.mx;
    const ctx = mx.ctx;
    if (!ctx || !mx.running) return;
    const g = ctx.createGain();
    g.gain.value = level * (this.ctxState.driving ? 0.7 : 1);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = this.ctxState.driving ? Math.min(cutoff, 2200) : cutoff;
    const pan = ctx.createStereoPanner();
    pan.pan.value = r(-0.85, 0.85);
    g.connect(lp).connect(pan).connect(mx.buses.ambience);
    build(g, ctx.currentTime + 0.02, pan);
    const timer = setTimeout(
      () => {
        this.cleanups.delete(timer);
        g.disconnect();
        lp.disconnect();
        pan.disconnect();
      },
      (life + 0.5) * 1000,
    );
    this.cleanups.add(timer);
  }

  /** FM chirp (gull / bird tweet): carrier sweep with a fast modulator. */
  private fmChirp(dest: AudioNode, t: number, f0: number, f1: number, dur: number, modHz: number, depth: number, gain: number): void {
    const mx = this.mx;
    const ctx = mx.ctx;
    if (!ctx || !mx.canSpawn()) return;
    const car = ctx.createOscillator();
    const mod = ctx.createOscillator();
    const md = ctx.createGain();
    const g = ctx.createGain();
    car.frequency.setValueAtTime(f0, t);
    car.frequency.linearRampToValueAtTime(f1, t + dur);
    mod.frequency.value = modHz;
    md.gain.value = depth;
    mod.connect(md).connect(car.frequency);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(gain, t + dur * 0.25);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    car.connect(g).connect(dest);
    mx.voiceCount += 2;
    car.onended = () => {
      mx.voiceCount -= 2;
      for (const n of [car, mod, md, g]) n.disconnect();
    };
    car.start(t);
    mod.start(t);
    car.stop(t + dur + 0.03);
    mod.stop(t + dur + 0.03);
  }

  /** A shouted syllable from across the street: a saw through a sweeping formant, loud and short. */
  private shout(dest: AudioNode, t: number, f0: number, f0End: number, f1: number, f1End: number, dur: number, gain: number): void {
    const mx = this.mx;
    tone(mx, { type: 'sawtooth', f0, f1: f0End, sweepTime: dur, t, dur, gain, attack: 0.03, release: dur * 0.3, bp: f1, bpTo: f1End, q: 3, dest });
    tone(mx, { type: 'sawtooth', f0, f1: f0End, sweepTime: dur, t, dur, gain: gain * 0.5, attack: 0.03, release: dur * 0.3, bp: f1 * 2.4, bpTo: f1End * 2.2, q: 4, dest });
  }

  /** St Brannoc's (somewhere over the roofs) striking the hour: 1-12 slow, soft strokes. */
  churchBell(hour: number): void {
    this.lastEvent = 'churchBell';
    const mx = this.mx;
    const strokes = ((Math.floor(hour) + 11) % 12) + 1;
    const near = 0.45 + 0.55 * (this.weights['mercy-square'] ?? (this.ctxState.district === 'mercy-square' ? 1 : 0));
    this.chain(0.22 * near, strokes * 1.6 + 3, 2400, (d, t) => {
      for (let i = 0; i < strokes; i++) bellPartials(mx, d, t + i * 1.6, 293.7, 3.2, 0.4, [0.5, 1, 1.19, 1.5, 2, 2.51], true);
    });
  }

  play(e: AmbientEvent): void {
    this.lastEvent = e;
    const mx = this.mx;
    switch (e) {
      case 'shipHorn':
        this.chain(0.38, 2.2, 900, (d, t) => {
          const f = r(0.9, 1.1);
          tone(mx, { type: 'sawtooth', f0: 98 * f, t, dur: 1.7, gain: 0.3, attack: 0.45, release: 0.55, lp: 700, dest: d });
          tone(mx, { type: 'square', f0: 130 * f, t, dur: 1.7, gain: 0.16, attack: 0.5, release: 0.55, lp: 600, dest: d });
          tone(mx, { type: 'sine', f0: 49 * f, t, dur: 1.7, gain: 0.25, attack: 0.4, release: 0.55, dest: d });
        });
        break;
      case 'train':
        this.chain(0.7, 6.5, 2600, (d, t, pan) => {
          const ctx = mx.ctx as AudioContext;
          pan.pan.setValueAtTime(-0.8, t);
          pan.pan.linearRampToValueAtTime(0.8, t + 5.5);
          noiseBurst(mx, { kind: 'brown', t, dur: 5.5, gain: 0.7, attack: 2.4, release: 2.2, filter: 'lowpass', f0: 150, dest: d });
          // Clickety-clack over the arches: one noise voice, automated pulses in axle pairs.
          const g = ctx.createGain();
          g.gain.value = 0;
          g.connect(d);
          const bp = ctx.createBiquadFilter();
          bp.type = 'bandpass';
          bp.frequency.value = 1700;
          bp.Q.value = 2;
          const src = mx.noiseSource('white');
          src.connect(bp).connect(g);
          const pairs = 15;
          for (let i = 0; i < pairs; i++) {
            const swell = Math.sin(((i + 0.5) / pairs) * Math.PI);
            for (const off of [0, 0.085]) {
              const tt = t + 0.4 + i * 0.3 + off;
              g.gain.setValueAtTime(0.0001, tt);
              g.gain.linearRampToValueAtTime(0.5 * swell * (off ? 0.7 : 1), tt + 0.004);
              g.gain.exponentialRampToValueAtTime(0.0001, tt + 0.05);
            }
          }
          mx.voiceCount++;
          src.onended = () => {
            mx.voiceCount--;
            for (const n of [src, bp, g]) n.disconnect();
          };
          mx.startNoise(src, t);
          src.stop(t + 5.3);
          // The whistle, once in a while.
          if (Math.random() < 0.3) tone(mx, { type: 'square', f0: 620, t: t + 1.2, dur: 0.9, gain: 0.08, attack: 0.08, release: 0.3, lp: 1800, dest: d });
        });
        break;
      case 'gull':
        this.chain(0.35, 2, 7000, (d, t) => {
          const n = Math.floor(r(2, 4));
          let at = t;
          for (let i = 0; i < n; i++) {
            const base = r(1700, 2300);
            this.fmChirp(d, at, base, base * 1.45, r(0.16, 0.24), r(30, 45), r(300, 450), 0.5);
            at += r(0.25, 0.4);
          }
        });
        break;
      case 'birds':
        this.chain(0.25, 2, 9000, (d, t) => {
          const n = Math.floor(r(4, 8));
          let at = t;
          const base = r(3000, 4200);
          for (let i = 0; i < n; i++) {
            this.fmChirp(d, at, base * r(0.9, 1.15), base * r(1.1, 1.4), r(0.05, 0.1), r(60, 110), r(500, 900), 0.5);
            at += r(0.09, 0.2);
          }
        });
        break;
      case 'dog':
        this.chain(0.3, 2, 2200, (d, t) => {
          const n = Math.floor(r(2, 4));
          for (let i = 0; i < n; i++) {
            const at = t + i * r(0.3, 0.42);
            tone(mx, { type: 'sawtooth', f0: r(230, 270), f1: 130, sweepTime: 0.12, t: at, dur: 0.15, gain: 0.5, attack: 0.01, bp: 900, q: 3, dest: d });
            noiseBurst(mx, { kind: 'white', t: at, dur: 0.1, gain: 0.25, filter: 'bandpass', f0: 1100, q: 2, dest: d });
          }
        });
        break;
      case 'farSiren':
        this.chain(0.18, 4.2, 1400, (d, t) => {
          // A distant two-tone, as the borough's other patrols would sound.
          for (let i = 0; i < 4; i++) {
            const at = t + i * 0.55;
            tone(mx, { type: 'sawtooth', f0: i % 2 ? 490 : 640, t: at, dur: 0.56, gain: 0.22 * Math.sin(((i + 0.5) / 4) * Math.PI), attack: 0.04, release: 0.06, lp: 1400, dest: d });
          }
        });
        break;
      case 'clank':
        // A yard or a crane somewhere: a heavy metal clank and its ring, sometimes a second.
        this.chain(0.3, 2.5, 3000, (d, t) => {
          const n = Math.random() < 0.4 ? 2 : 1;
          for (let i = 0; i < n; i++) {
            const at = t + i * r(0.35, 0.7);
            tone(mx, { type: 'sine', f0: 90, f1: 45, sweepTime: 0.12, t: at, dur: 0.25, gain: 0.5, dest: d });
            bellPartials(mx, d, at, r(180, 260), 1.2, 0.18, [1, 2.31, 3.9, 5.7]);
          }
        });
        break;
      case 'laugh':
        // A burst of laughter from a pub door: a few falling formant "ha"s over the murmur.
        this.chain(0.22, 1.6, 2600, (d, t) => {
          const f = r(180, 260);
          const n = Math.floor(r(3, 6));
          for (let i = 0; i < n; i++) {
            const at = t + i * r(0.11, 0.16);
            tone(mx, { type: 'sawtooth', f0: f * (1 - i * 0.04), f1: f * 0.85 * (1 - i * 0.04), sweepTime: 0.09, t: at, dur: 0.1, gain: 0.35, attack: 0.012, bp: r(700, 1000), q: 3, dest: d });
          }
          ringTick(mx, d, t + r(0.1, 0.6), r(2500, 3200), 14, 0.08, 0.12); // a glass
        });
        break;
      case 'rope':
        // Mooring ropes working against the quay: slow squeaks, the hull nudging the timber.
        this.chain(0.28, 2.4, 2400, (d, t) => {
          const n = Math.floor(r(2, 4));
          for (let i = 0; i < n; i++) {
            const at = t + i * r(0.45, 0.8);
            const f = r(380, 620);
            tone(mx, { type: 'sawtooth', f0: f, f1: f * r(1.15, 1.5), sweepTime: 0.3, t: at, dur: r(0.25, 0.45), gain: 0.16, attack: 0.08, release: 0.1, bp: f * 2, bpTo: f * 2.6, q: 6, dest: d });
          }
          tone(mx, { type: 'sine', f0: 70, f1: 45, sweepTime: 0.3, t: t + r(0.2, 0.9), dur: 0.5, gain: 0.35, attack: 0.02, dest: d });
        });
        break;
      case 'crane':
        // A crane taking a load: the motor winds up, a chain rattles, the load lands with a boom.
        this.chain(0.3, 4.5, 2600, (d, t) => {
          tone(mx, { type: 'sawtooth', f0: 70, f1: 120, sweepTime: 1.6, t, dur: 2.8, gain: 0.2, attack: 0.5, release: 0.8, lp: 600, q: 1.5, dest: d });
          for (let i = 0; i < 14; i++) ringTick(mx, d, t + 0.4 + i * r(0.1, 0.2), r(1800, 3200), 4, 0.05, 0.03);
          tone(mx, { type: 'sine', f0: 80, f1: 40, sweepTime: 0.25, t: t + 3.1, dur: 0.6, gain: 0.5, dest: d });
          bellPartials(mx, d, t + 3.1, r(160, 220), 1.4, 0.15, [1, 2.31, 3.9]);
        });
        break;
      case 'barrow':
        // A barrow boy on the Parade: two or three shouted syllables, the last one long.
        this.chain(0.26, 2, 3200, (d, t) => {
          const f0 = r(150, 210);
          const n = Math.floor(r(2, 4));
          let at = t;
          for (let i = 0; i < n; i++) {
            const last = i === n - 1;
            const dur = last ? r(0.35, 0.55) : r(0.12, 0.2);
            this.shout(d, at, f0 * (last ? 1.25 : 1), f0 * (last ? 0.85 : 1.1), r(500, 800), r(600, 1100), dur, 0.26);
            at += dur + r(0.03, 0.08);
          }
        });
        break;
      case 'pubDoor':
        // A pub door swings: the murmur and a snatch of the band spill out, then the door thuds shut.
        this.chain(0.3, 2.6, 3000, (d, t) => {
          noiseBurst(mx, { kind: 'pink', t, dur: 1.6, gain: 0.22, attack: 0.08, release: 0.5, filter: 'bandpass', f0: 800, q: 1.2, dest: d });
          noiseBurst(mx, { kind: 'pink', t: t + 0.05, dur: 1.5, gain: 0.12, attack: 0.1, release: 0.5, filter: 'bandpass', f0: 1800, q: 2, dest: d });
          tone(mx, { type: 'sine', f0: 120, f1: 60, sweepTime: 0.08, t: t + 1.55, dur: 0.22, gain: 0.5, dest: d });
          noiseBurst(mx, { kind: 'brown', t: t + 1.55, dur: 0.08, gain: 0.3, filter: 'lowpass', f0: 700, dest: d });
          if (Math.random() < 0.5) ringTick(mx, d, t + r(0.3, 1.2), r(2400, 3000), 12, 0.07, 0.1);
        });
        break;
      case 'children':
        // Children playing in the gardens: high, quick calls and a laugh.
        this.chain(0.2, 2.4, 5000, (d, t) => {
          const n = Math.floor(r(3, 6));
          let at = t;
          for (let i = 0; i < n; i++) {
            const f0 = r(290, 400);
            this.shout(d, at, f0, f0 * r(0.8, 1.2), r(600, 900), r(700, 1200), r(0.08, 0.2), 0.14);
            at += r(0.12, 0.4);
          }
        });
        break;
      case 'bottles':
        // Milk bottles on a step: glass chinks.
        this.chain(0.25, 1.4, 6000, (d, t) => {
          const n = Math.floor(r(2, 5));
          for (let i = 0; i < n; i++) ringTick(mx, d, t + i * r(0.08, 0.2), r(2800, 4200), 18, 0.1, 0.14);
        });
        break;
    }
  }

  dispose(): void {
    for (const t of this.cleanups) clearTimeout(t);
    this.cleanups.clear();
    if (this.beds) {
      for (const n of this.beds.nodes) {
        try {
          if (n instanceof AudioScheduledSourceNode) n.stop();
        } catch {
          /* not started */
        }
        try {
          n.disconnect();
        } catch {
          /* gone */
        }
      }
      this.beds = null;
    }
  }
}
